<template>
  <Teleport to="body">
    <div v-if="show" class="fcom_modal_overlay" @click.self="$emit('close')">
      <div class="fcom_modal">
        <!-- Header -->
        <div class="fcom_modal_header">
          <h3>{{ title || __('choose_icon') }}</h3>
          <button type="button" class="fcom_modal_close" @click="$emit('close')">×</button>
        </div>

        <!-- Body -->
        <div class="fcom_modal_body">
          <!-- Preview + tab switcher -->
          <div class="fcom_icon_preview_area">
            <div class="fcom_icon_preview_box" v-html="previewHtml || currentIcon || '📅'" />
            <span class="fcom_icon_preview_label">{{ __('preview') }}</span>
          </div>

          <div class="fcom_icon_picker_tabs">
            <button type="button" :class="{ active: activeTab === 'emoji' }" @click="activeTab = 'emoji'">
              {{ __('emoji') }}
            </button>
            <button type="button" :class="{ active: activeTab === 'svg' }" @click="activeTab = 'svg'">
              {{ __('custom_svg') }}
            </button>
          </div>

          <!-- Emoji grid -->
          <div v-if="activeTab === 'emoji'">
            <div class="fcom_emoji_custom_row">
              <input
                v-model="customEmojiInput"
                type="text"
                class="fcom_emoji_custom_input"
                :placeholder="__('type_or_paste_emoji')"
                @input="onCustomEmojiInput"
              />
            </div>
            <div class="fcom_emoji_grid">
              <button
                v-for="emoji in emojis"
                :key="emoji"
                type="button"
                :class="['fcom_emoji_option', { 'is-selected': selectedEmoji === emoji }]"
                @click="selectEmoji(emoji)"
              >
                {{ emoji }}
              </button>
            </div>
          </div>

          <!-- SVG paste area -->
          <div v-if="activeTab === 'svg'" class="fcom_svg_tab">
            <label class="fcom_svg_label">{{ __('paste_svg_code') }}</label>
            <textarea
              v-model="svgInput"
              class="fcom_svg_textarea"
              rows="6"
              :placeholder="__('svg_placeholder')"
              @input="onSvgInput"
            />
            <p class="fcom_svg_hint">{{ __('svg_hint') }}</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="fcom_modal_footer">
          <button type="button" class="el-button fcom_secondary_button" @click="$emit('close')">
            {{ __('cancel') }}
          </button>
          <button type="button" class="el-button fcom_primary_button" @click="applySelection">
            {{ __('apply') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import { __ } from '@/utils/i18n.js'

const props = defineProps({
  show:        Boolean,
  title:       String,
  currentIcon: { type: String, default: '📅' },
})

const emit = defineEmits(['close', 'select'])

const activeTab       = ref('emoji')
const selectedEmoji   = ref('')
const svgInput        = ref('')
const previewHtml     = ref('')
const customEmojiInput = ref('')

const emojis = [
  '📅','🗓️','📆','🎉','🎊','🎈','🎪','🎭','🎵','🎶',
  '🏆','⭐','🌟','💫','✨','🔥','💡','📣','📢','🔔',
  '🎯','🚀','💬','🤝','👥','🏠','🏢','🌍','🌎','🌏',
  '☀️','🌙','⚡','🎓','📚','✏️','🖊️','📌','📍','🗺️',
  '🍕','🍔','☕','🍿','🎮','🏃','🚴','⚽','🏀','🎾',
  '❤️','💙','💚','💛','💜','🧡','🤍','🖤','💎','🎁',
]

watch(() => props.show, (val) => {
  if (!val) return
  if (props.currentIcon?.includes('<svg')) {
    activeTab.value      = 'svg'
    svgInput.value       = props.currentIcon
    previewHtml.value    = props.currentIcon
    selectedEmoji.value  = ''
    customEmojiInput.value = ''
  } else {
    activeTab.value      = 'emoji'
    selectedEmoji.value  = props.currentIcon || '📅'
    svgInput.value       = ''
    previewHtml.value    = ''
    customEmojiInput.value = ''
  }
})

function selectEmoji(emoji) {
  selectedEmoji.value  = emoji
  customEmojiInput.value = ''
  previewHtml.value    = ''
  svgInput.value       = ''
}

function onCustomEmojiInput() {
  const val = customEmojiInput.value.trim()
  if (val) {
    selectedEmoji.value = val
    previewHtml.value   = ''
    svgInput.value      = ''
  }
}

function onSvgInput() {
  if (svgInput.value.includes('<svg')) {
    previewHtml.value = svgInput.value
    selectedEmoji.value = ''
  }
}

function applySelection() {
  if (activeTab.value === 'svg' && svgInput.value.includes('<svg')) {
    emit('select', svgInput.value.trim())
  } else if (selectedEmoji.value) {
    emit('select', selectedEmoji.value)
  }
  emit('close')
}
</script>
